import {
  selectActiveVirtualBackground,
  selectVirtualBackgroundStatus,
} from 'features/virtual-backgrounds/selectors';
import { useCallback, useEffect, useReducer, useRef } from 'react';
import { initialState, reducer } from 'utils/broadcast-setup/reducer';
import { usePendingUserMediaWatchdog } from 'utils/broadcast-setup/usePendingUserMediaWatchdog';
import { useMediaDevices } from 'hooks/useMediaDevices';
import { isDomException, isValidVBSelection } from 'utils/types';
import { logger } from 'utils/logger';
import { getDefaultPromptedDevice } from 'features/user-media/utils/getDefaultPromptedDevice';
import {
  getDefaultActiveDevices,
  getDevicePromptConstraints,
  stopStreamTracks,
} from 'features/user-media/utils';
import * as Sentry from '@sentry/react';
import { refineMediaDevices } from 'features/user-media/utils/refineMediaDevices';
import webrtcAdapter from 'webrtc-adapter';
import { getUserMedia } from 'features/user-media/utils/getUserMedia';
import {
  activeMediaDeviceUpdated,
  facingModeChanged,
  selectJoinMediaDefaults,
} from 'features/user-media/userMediaSlice';
import { activateVirtualBackground } from 'features/virtual-backgrounds/thunks/activateVirtualBackground';
import { useUserAudioOutput } from 'hooks/useUserAudioOutput';
import { useSingularUserMedia } from 'hooks/useSingularUserMedia';
import { useVirtualBackground } from 'hooks/useVirtualBackground';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  virtualBackgroundReset,
  virtualBackgroundSelected,
} from 'features/virtual-backgrounds/virtualBackgroundsSlice';
import { changeVirtualBackground } from 'features/virtual-backgrounds/thunks/changeVirtualBackground';
import {
  SelectedVirtualBackground,
  VirtualBackgroundSyncConfig,
} from 'features/virtual-backgrounds/types';
import { usePublishingFeed } from 'hooks/usePublishingFeed';
import { PublishingFeed } from 'utils/webrtc/publishing/PublishingFeed';
import { usePermissions } from 'features/permissions/usePermissions';
import { PermissionTypes } from 'features/permissions/types';
import { BroadcastSetupReason } from 'features/room/types';
import { selectIsAudioOnlyRoom } from 'features/room/roomSlice';

interface BroadcastSetupMediaOptions {
  reason?: BroadcastSetupReason;
}

// @TODO think of how to make this function more flexible in terms of using broadcast permissions
export const useBroadcastSetupMedia = (options: BroadcastSetupMediaOptions = {}) => {
  const appDispatch = useAppDispatch();

  const isMounted = useRef(false);

  const feed = usePublishingFeed('media');
  const publishingFeed = feed.control as PublishingFeed;

  const [state, dispatch] = useReducer(reducer, initialState);

  const audioOutputHandler = useUserAudioOutput();
  const requestSingularUserMedia = useSingularUserMedia();

  const { control: VB } = useVirtualBackground();
  const selectedBackground = useAppSelector(selectActiveVirtualBackground);

  const joinMediaDefaults = useAppSelector(selectJoinMediaDefaults);
  const VBStatus = useAppSelector(selectVirtualBackgroundStatus);

  const VBApplied = useRef(false);

  const { hasPermissions } = usePermissions();
  const allowVideoBroadcast = hasPermissions(PermissionTypes.broadcastVideo);
  const allowAudioBroadcast = hasPermissions(PermissionTypes.broadcastAudio);
  const isAudioOnlyRoom = useAppSelector(selectIsAudioOnlyRoom);

  let videoPermission = allowVideoBroadcast;
  let audioPermission = allowAudioBroadcast;

  if (options.reason === 'breakout') {
    audioPermission = true;

    if (!isAudioOnlyRoom) {
      videoPermission = true;
    }
  }

  const broadcastsPermissions = {
    video: videoPermission,
    audio: audioPermission,
  };

  // A mid-session promotion and a move into a breakout room are both explicit moderator acts, so
  // the setup opens with the media about to be published rather than the account's join defaults.
  const mediaDefaults =
    options.reason === 'promotion' || options.reason === 'breakout'
      ? { audio: audioPermission, video: videoPermission && !isAudioOnlyRoom }
      : joinMediaDefaults;

  const {
    updateMediaDevices,
    mediaDevices,
    activeMediaDevices,
    camPermissions,
    micPermissions,
    persistDevices,
  } = useMediaDevices();
  const pendingMedia = usePendingUserMediaWatchdog(dispatch, mediaDevices);

  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (micPermissions === 'granted') {
      dispatch({ type: 'mediaUnblocked', payload: 'audio' });
    } else {
      dispatch({ type: 'mediaDeactivated', payload: 'audio' });
    }
  }, [micPermissions]);

  useEffect(() => {
    if (camPermissions === 'granted') {
      dispatch({ type: 'mediaUnblocked', payload: 'video' });
    } else {
      dispatch({ type: 'mediaDeactivated', payload: 'video' });
    }
  }, [camPermissions]);

  const handleMediaError = async (error: unknown) => {
    if (!isMounted.current) {
      return;
    }

    if (isDomException(error)) {
      logger.error(`${error.name}: ${error.message}`);

      const audioPromise = broadcastsPermissions.audio
        ? requestSingularUserMedia({
            audio: {
              deviceId: {
                exact: getDefaultPromptedDevice(mediaDevices, 'audioinput'),
              },
            },
          })
        : Promise.resolve(undefined);

      const videoPromise = broadcastsPermissions.video
        ? requestSingularUserMedia({
            video: {
              deviceId: {
                exact: getDefaultPromptedDevice(mediaDevices, 'videoinput'),
              },
            },
          })
        : Promise.resolve(undefined);

      const [audio, video] = await Promise.all([audioPromise, videoPromise]);

      await updateMediaDevices();

      if (video?.stream) {
        if (mediaDefaults.video) {
          publishingFeed.setJoinMedia(video.stream, 'video');
          dispatch({
            type: 'mediaUpdated',
            payload: { type: 'video', activateMedia: true },
          });
        } else {
          stopStreamTracks(video.stream);
        }
      }

      if (audio?.stream) {
        if (mediaDefaults.audio) {
          publishingFeed.setJoinMedia(audio.stream, 'audio');
          dispatch({
            type: 'mediaUpdated',
            payload: { type: 'audio', activateMedia: true },
          });
        } else {
          stopStreamTracks(audio.stream);
        }
      }

      if (audio?.stream || video?.stream) {
        dispatch({ type: 'statusUpdated', payload: 'success' });
      } else if (
        audio?.error.name === 'NotAllowedError' &&
        video?.error.name === 'NotAllowedError'
      ) {
        dispatch({ type: 'statusUpdated', payload: 'denied' });
      } else {
        dispatch({ type: 'statusUpdated', payload: 'failure' });
      }
    } else {
      logger.error(error);
      Sentry.captureException(error);
      dispatch({ type: 'statusUpdated', payload: 'failure' });
    }
  };

  // @TODO Firefox requires separate permissions for each device. This case is unhandled.
  const requestPermissions = async () => {
    try {
      const refineRequest = refineMediaDevices(updateMediaDevices, {
        permissions: broadcastsPermissions,
      });

      // The in-room setup modals render no stalled state, so only the join screen is watched.
      const updatedMediaDevices =
        options.reason === 'join'
          ? await pendingMedia.observe(refineRequest, 'join-screen')
          : await refineRequest;

      if (!updatedMediaDevices) {
        return;
      }

      // check against the media defaults from the backend
      // if all values are false, obtain only device permissions and list of devices
      if (!mediaDefaults.video && !mediaDefaults.audio) {
        if (webrtcAdapter.browserDetails.browser === 'firefox') {
          const constraints = getDevicePromptConstraints(
            updatedMediaDevices,
            broadcastsPermissions
          );

          if (constraints.audio === false && constraints.video === false) {
            dispatch({ type: 'statusUpdated', payload: 'denied' });
            return;
          }

          const stream = await getUserMedia(constraints);

          if (!isMounted.current) {
            stopStreamTracks(stream);
            return;
          }

          const activeDevices = getDefaultActiveDevices(updatedMediaDevices);

          await updateMediaDevices({
            updatePermissions: true,
            updateActiveDevices: true,
            activeDevices,
          });
          stopStreamTracks(stream);

          dispatch({ type: 'statusUpdated', payload: 'success' });
          return;
        }

        if (!isMounted.current) {
          return;
        }

        const activeDevices = getDefaultActiveDevices(updatedMediaDevices);

        await updateMediaDevices({
          updatePermissions: true,
          updateActiveDevices: true,
          activeDevices,
        });

        const constraints = getDevicePromptConstraints(updatedMediaDevices, broadcastsPermissions);
        if (constraints.audio === false && constraints.video === false) {
          dispatch({ type: 'statusUpdated', payload: 'denied' });
          return;
        }

        dispatch({ type: 'statusUpdated', payload: 'success' });
        return;
      }

      const constraints = getDevicePromptConstraints(updatedMediaDevices, broadcastsPermissions);

      if (constraints.audio === false && constraints.video === false) {
        dispatch({ type: 'statusUpdated', payload: 'denied' });
        return;
      }

      const stream = await getUserMedia(constraints);

      if (!isMounted.current) {
        stopStreamTracks(stream);
        return;
      }

      const activeDevices = getDefaultActiveDevices(updatedMediaDevices);

      await updateMediaDevices({
        updatePermissions: true,
        updateActiveDevices: true,
        activeDevices,
      });

      if (mediaDefaults.video) {
        const videoTrack = stream.getVideoTracks()[0];
        if (videoTrack) {
          const newVideoStream = new MediaStream();
          newVideoStream.addTrack(videoTrack.clone());

          const settings = videoTrack.getSettings();

          if (settings.facingMode) {
            appDispatch(facingModeChanged(settings.facingMode));
          }
          publishingFeed.setJoinMedia(newVideoStream, 'video');
          dispatch({
            type: 'mediaUpdated',
            payload: { type: 'video', activateMedia: true },
          });
        }
      }

      if (mediaDefaults.audio) {
        const audioTrack = stream.getAudioTracks()[0];
        if (audioTrack) {
          const newAudioStream = new MediaStream();
          newAudioStream.addTrack(audioTrack.clone());
          publishingFeed.setJoinMedia(newAudioStream, 'audio');
          dispatch({
            type: 'mediaUpdated',
            payload: { type: 'audio', activateMedia: true },
          });
        }
      }

      stopStreamTracks(stream);

      dispatch({ type: 'statusUpdated', payload: 'success' });
    } catch (error) {
      await handleMediaError(error);
    }
  };

  const disableCamera = useCallback(async () => {
    if (!publishingFeed.joinMedia.video) {
      return;
    }

    dispatch({ type: 'mediaUpdated', payload: { type: 'video', processing: true } });

    try {
      await VB.deactivate();

      publishingFeed.cleanupJoinMedia('video');
    } finally {
      dispatch({
        type: 'mediaUpdated',
        payload: { type: 'video', processing: false },
      });
    }
  }, [publishingFeed, VB]);

  const enableCamera = useCallback(
    async (deviceId?: string) => {
      dispatch({ type: 'mediaUpdated', payload: { type: 'video', processing: true } });

      try {
        const { stream } = await requestSingularUserMedia({
          video: {
            deviceId: {
              exact: deviceId || activeMediaDevices.videoinput,
            },
          },
        });

        if (!stream) {
          dispatch({
            type: 'mediaUpdated',
            payload: { type: 'video', active: false, disabled: true, processing: false },
          });
          return;
        }

        let updatedStream = stream;

        if (selectedBackground.value && !VB.activated) {
          const vbStream = await appDispatch(
            activateVirtualBackground({
              stream,
              selectedBackground,
            })
          );

          if (vbStream) {
            updatedStream = vbStream;
          }
        }

        publishingFeed.setJoinMedia(updatedStream, 'video');
        dispatch({
          type: 'mediaUpdated',
          payload: { type: 'video', active: true, disabled: false, processing: false },
        });
      } catch (error) {
        dispatch({
          type: 'mediaUpdated',
          payload: { type: 'video', processing: false },
        });
        throw error;
      }
    },
    [
      requestSingularUserMedia,
      activeMediaDevices.videoinput,
      selectedBackground,
      VB,
      appDispatch,
      publishingFeed,
    ]
  );

  const disableAudio = useCallback(() => {
    if (!publishingFeed.joinMedia.audio) {
      return;
    }

    dispatch({ type: 'mediaUpdated', payload: { type: 'audio', processing: true } });

    try {
      publishingFeed.cleanupJoinMedia('audio');
    } finally {
      dispatch({
        type: 'mediaUpdated',
        payload: { type: 'audio', processing: false },
      });
    }
  }, [publishingFeed]);

  const handleVideoToggle = useCallback(
    async (checked: boolean) => {
      if (state.video.processing) {
        return;
      }

      dispatch({ type: 'setMediaActive', payload: { type: 'video', value: checked } });

      if (publishingFeed.joinMedia.video) {
        await disableCamera();
      } else {
        await enableCamera();
      }
    },
    [state.video.processing, publishingFeed, disableCamera, enableCamera]
  );

  const enableAudio = useCallback(
    async (deviceId?: string) => {
      dispatch({ type: 'mediaUpdated', payload: { type: 'audio', processing: true } });

      try {
        const { stream } = await requestSingularUserMedia({
          audio: {
            deviceId: {
              exact: deviceId || activeMediaDevices.audioinput,
            },
          },
        });

        if (!stream) {
          dispatch({
            type: 'mediaUpdated',
            payload: { type: 'audio', active: false, disabled: true, processing: false },
          });
          return;
        }

        publishingFeed.setJoinMedia(stream, 'audio');
        dispatch({
          type: 'mediaUpdated',
          payload: { type: 'audio', active: true, disabled: false, processing: false },
        });
      } catch (error) {
        dispatch({
          type: 'mediaUpdated',
          payload: { type: 'audio', processing: false },
        });
        throw error;
      }
    },
    [requestSingularUserMedia, activeMediaDevices.audioinput, publishingFeed]
  );

  const handleAudioToggle = useCallback(
    async (checked: boolean) => {
      if (state.audio.processing) {
        return;
      }

      dispatch({ type: 'setMediaActive', payload: { type: 'audio', value: checked } });

      if (publishingFeed.joinMedia.audio) {
        disableAudio();
      } else {
        await enableAudio();
      }
    },
    [state.audio.processing, publishingFeed, disableAudio, enableAudio]
  );

  const handleAudioInputChange = useCallback(
    async (deviceId: string) => {
      const currentDeviceId = publishingFeed.joinMedia.audio
        ?.getAudioTracks()[0]
        .getSettings().deviceId;

      if (currentDeviceId === deviceId) {
        return;
      }

      persistDevices(deviceId, 'audioinput');

      if (!state.audio.active) {
        appDispatch(
          activeMediaDeviceUpdated({
            kind: 'audioinput',
            id: deviceId,
          })
        );

        return;
      }

      dispatch({ type: 'mediaUpdated', payload: { type: 'audio', processing: true } });

      try {
        if (publishingFeed.joinMedia.audio) {
          stopStreamTracks(publishingFeed.joinMedia.audio);
        }

        const { stream } = await requestSingularUserMedia({
          audio: {
            deviceId: {
              exact: deviceId,
            },
          },
        });

        if (!stream) {
          dispatch({ type: 'mediaDeactivated', payload: 'audio' });
          return;
        }

        publishingFeed.setJoinMedia(stream, 'audio');
      } finally {
        dispatch({ type: 'mediaUpdated', payload: { type: 'audio', processing: false } });
      }
    },
    [publishingFeed, persistDevices, state.audio.active, appDispatch, requestSingularUserMedia]
  );

  const handleAudioOutputChange = useCallback(
    async (deviceId: string) => {
      persistDevices(deviceId, 'audiooutput');
      await audioOutputHandler(deviceId);
    },
    [persistDevices, audioOutputHandler]
  );

  const handleVideoInputChange = useCallback(
    async (deviceId: string) => {
      const currentDeviceId = publishingFeed.joinMedia.video
        ?.getVideoTracks()[0]
        .getSettings().deviceId;
      if (currentDeviceId === deviceId) {
        return;
      }

      persistDevices(deviceId, 'videoinput');

      if (!state.video.active) {
        appDispatch(
          activeMediaDeviceUpdated({
            kind: 'videoinput',
            id: deviceId,
          })
        );

        return;
      }

      dispatch({ type: 'mediaUpdated', payload: { type: 'video', processing: true } });

      try {
        if (publishingFeed.joinMedia.video) {
          stopStreamTracks(publishingFeed.joinMedia.video);
        }

        const { stream } = await requestSingularUserMedia({
          video: {
            deviceId: {
              exact: deviceId,
            },
          },
        });

        if (!stream) {
          dispatch({ type: 'mediaDeactivated', payload: 'video' });
          return;
        }

        let updatedStream = stream;

        if (VB.activated) {
          const processed = await VB.updateStream({
            stream,
          });
          if (processed) {
            updatedStream = processed;
          }
        }

        const videoTrack = stream.getVideoTracks()[0];
        if (videoTrack) {
          const settings = videoTrack.getSettings();

          if (settings.facingMode) {
            appDispatch(facingModeChanged(settings.facingMode));
          }
        }

        publishingFeed.setJoinMedia(updatedStream, 'video');
      } finally {
        dispatch({
          type: 'mediaUpdated',
          payload: { type: 'video', processing: false },
        });
      }
    },
    [publishingFeed, persistDevices, state.video.active, appDispatch, requestSingularUserMedia, VB]
  );

  const resetVB = useCallback(async () => {
    VBApplied.current = false;

    appDispatch(virtualBackgroundReset());

    if (!publishingFeed.joinMedia.video) {
      return;
    }

    if (VBStatus === 'updating') {
      return;
    }

    dispatch({ type: 'mediaUpdated', payload: { type: 'video', processing: true } });

    try {
      const { stream } = await requestSingularUserMedia({
        video: {
          deviceId: {
            exact: activeMediaDevices.videoinput,
          },
        },
      });

      await VB.deactivate();
      stopStreamTracks(publishingFeed.joinMedia.video);

      if (!stream) {
        dispatch({
          type: 'mediaUpdated',
          payload: { type: 'video', active: false, disabled: true, processing: false },
        });
        return;
      }

      publishingFeed.setJoinMedia(stream, 'video');
      dispatch({
        type: 'mediaUpdated',
        payload: { type: 'video', active: true, disabled: false, processing: false },
      });
    } catch (error) {
      dispatch({
        type: 'mediaUpdated',
        payload: { type: 'video', processing: false },
      });
      throw error;
    }
  }, [
    appDispatch,
    publishingFeed,
    VBStatus,
    requestSingularUserMedia,
    activeMediaDevices.videoinput,
    VB,
  ]);

  const changeVB = useCallback(
    async (config: SelectedVirtualBackground) => {
      VBApplied.current = true;

      if (!publishingFeed.joinMedia.video) {
        appDispatch(virtualBackgroundSelected(config));
        return;
      }

      if (VB.activated) {
        appDispatch(changeVirtualBackground(config));
        return;
      }

      dispatch({ type: 'mediaUpdated', payload: { type: 'video', processing: true } });

      try {
        const stream = await appDispatch(
          activateVirtualBackground({
            stream: publishingFeed.joinMedia.video,
            selectedBackground: config,
          })
        );

        if (stream) {
          publishingFeed.setJoinMedia(stream, 'video');
        }
      } finally {
        dispatch({ type: 'mediaUpdated', payload: { type: 'video', processing: false } });
      }
    },
    [publishingFeed, appDispatch, VB]
  );

  const changeVBEffect = useCallback(
    (stream: MediaStream | null, syncConfig: VirtualBackgroundSyncConfig) => {
      if (stream && isValidVBSelection(syncConfig)) {
        changeVB(syncConfig as SelectedVirtualBackground);
      } else if (VBApplied.current && syncConfig === false) {
        resetVB();
      }
    },
    [changeVB, resetVB]
  );

  const cleanupBroadcastMedia = useCallback(() => {
    publishingFeed.cleanupJoinMedia();
  }, [publishingFeed]);

  return {
    state,
    onVideoToggle: handleVideoToggle,
    onAudioToggle: handleAudioToggle,
    onAudioInputChange: handleAudioInputChange,
    onAudioOutputChange: handleAudioOutputChange,
    onVideoInputChange: handleVideoInputChange,
    enableAudio,
    disableAudio,
    disableCamera,
    enableCamera,
    dispatch,
    requestPermissions,
    pendingMedia,
    changeVB,
    resetVB,
    changeVBEffect,
    videoStream: publishingFeed.joinMedia.video,
    audioStream: publishingFeed.joinMedia.audio,
    cleanupBroadcastMedia,
  };
};
